import React, { useEffect, useMemo, useState } from "react";
import { http, API } from "../lib/api";
import { applyCandidateFilters, filtersActive, filterSummary } from "../lib/filters";
import { CalendarBlank, Plus, Video, MapPin, X, Users, PencilSimple, Trash, DownloadSimple } from "@phosphor-icons/react";

export default function ScheduleInterviews({ statusFilter = "All", locationFilter = "All", requisitionFilter = "All" }) {
  const [candidates, setCandidates] = useState([]);
  const [interviews, setInterviews] = useState([]);
  const [panels, setPanels] = useState([]);
  const [open, setOpen] = useState(false);
  const [panelsOpen, setPanelsOpen] = useState(false);

  const refresh = async () => {
    const [c, i, p] = await Promise.all([
      http.get("/candidates"),
      http.get("/interviews"),
      http.get("/panels"),
    ]);
    setCandidates(c.data);
    setInterviews(i.data);
    setPanels(p.data);
  };
  useEffect(() => { refresh(); }, []);

  const visibleCandidates = useMemo(
    () => applyCandidateFilters(candidates, statusFilter, locationFilter, requisitionFilter),
    [candidates, statusFilter, locationFilter, requisitionFilter],
  );
  const visibleIds = useMemo(() => new Set(visibleCandidates.map((c) => c.id)), [visibleCandidates]);
  const visibleInterviews = useMemo(
    () => (filtersActive(statusFilter, locationFilter, requisitionFilter)
      ? interviews.filter((iv) => visibleIds.has(iv.candidateId))
      : interviews),
    [interviews, visibleIds, statusFilter, locationFilter, requisitionFilter],
  );

  return (
    <div className="space-y-5" data-testid="schedule-interviews-page">
      <div className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <div className="label-eyebrow">Interview Calendar</div>
          <h1 className="font-display text-3xl font-bold tracking-tight mt-1">Schedule Interviews</h1>
          <p className="text-slate-500 mt-1 text-sm">
            Plan panel interviews — candidate status auto-updates to &quot;Interview&quot;.
          </p>
        </div>
        <div className="flex gap-2">
          <button data-testid="panels-manage-btn" className="btn btn-outline" onClick={() => setPanelsOpen(true)}>
            <Users size={14} weight="bold" /> Manage Panels ({panels.length})
          </button>
          <button data-testid="schedule-new-btn" className="btn btn-primary" onClick={() => setOpen(true)}>
            <Plus size={14} weight="bold" /> New Interview
          </button>
        </div>
      </div>

      <div className="card overflow-hidden">
        {filtersActive(statusFilter, locationFilter, requisitionFilter) && (
          <div className="px-4 py-2 text-xs text-[#002FA7] bg-indigo-50 border-b flex items-center justify-between" data-testid="interviews-filter-active">
            <span>Sidebar filter — {filterSummary(statusFilter, locationFilter, requisitionFilter)}</span>
            <span className="font-mono">{visibleInterviews.length}/{interviews.length} interviews shown</span>
          </div>
        )}
        <table className="data-table" data-testid="interviews-table">
          <thead>
            <tr>
              <th>Candidate</th>
              <th>Position</th>
              <th>Date</th>
              <th>Time</th>
              <th>Mode</th>
              <th>Panel</th>
              <th style={{ width: 120 }}></th>
            </tr>
          </thead>
          <tbody>
            {visibleInterviews.length === 0 && (
              <tr><td colSpan={7} className="text-center text-slate-400 py-12">
                {interviews.length === 0
                  ? "No interviews scheduled yet. Click \"New Interview\" to add one."
                  : "No interviews match the sidebar filter."}
              </td></tr>
            )}
            {visibleInterviews.map((iv) => (
              <tr key={iv.id}>
                <td className="font-medium">{iv.candidateName}</td>
                <td>{iv.position}</td>
                <td className="font-mono text-xs">{iv.date}</td>
                <td className="font-mono text-xs">{iv.time}</td>
                <td>
                  <span className="inline-flex items-center gap-1 text-xs">
                    {iv.mode === "Video" ? <Video size={12} /> : <MapPin size={12} />} {iv.mode}
                  </span>
                </td>
                <td className="text-xs text-slate-600">{iv.panel?.join(", ") || "—"}</td>
                <td>
                  <a
                    href={`${API}/interviews/${iv.id}/ics`}
                    className="btn btn-outline !py-1 !px-2 !text-xs"
                    data-testid={`ics-download-${iv.id}`}
                    title="Download .ics calendar file"
                  >
                    <DownloadSimple size={12} /> .ics
                  </a>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {open && (
        <ScheduleModal
          candidates={visibleCandidates.length > 0 ? visibleCandidates : candidates}
          panels={panels}
          onClose={() => setOpen(false)}
          onSaved={async () => { await refresh(); setOpen(false); }}
        />
      )}
      {panelsOpen && (
        <PanelsManager
          panels={panels}
          onClose={() => setPanelsOpen(false)}
          onChanged={refresh}
        />
      )}
    </div>
  );
}

function ScheduleModal({ candidates, panels, onClose, onSaved }) {
  const [form, setForm] = useState({
    candidateId: candidates[0]?.id || "",
    date: new Date().toISOString().slice(0, 10),
    time: "10:00",
    mode: "Video",
    location: "",
    durationMinutes: 60,
    panelId: panels[0]?.id || "",
    panelStrOverride: "",
    notes: "",
  });
  const [saving, setSaving] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);

  const save = async () => {
    const c = candidates.find((x) => x.id === parseInt(form.candidateId, 10));
    if (!c) return;
    setSaving(true);
    setError(null);
    setResult(null);
    try {
      const payload = {
        candidateId: c.id,
        candidateName: c.name,
        position: c.position,
        date: form.date,
        time: form.time,
        mode: form.mode,
        location: form.location,
        durationMinutes: parseInt(form.durationMinutes, 10) || 60,
        notes: form.notes,
      };
      if (form.panelId) payload.panelId = form.panelId;
      if (form.panelStrOverride.trim()) payload.panel = form.panelStrOverride.split(",").map((s) => s.trim()).filter(Boolean);
      const r = await http.post("/interviews", payload);
      setResult(r.data);
      // small delay so user sees confirmation
      setTimeout(async () => { await onSaved(); }, 600);
    } catch (e) {
      setError(e.response?.data?.detail || e.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 grid place-items-center bg-black/40" data-testid="schedule-modal">
      <div className="card w-full max-w-lg p-6 max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between mb-4">
          <h2 className="font-display text-xl font-bold flex items-center gap-2">
            <CalendarBlank size={20} weight="fill" className="text-[#002FA7]" /> Schedule Interview
          </h2>
          <button onClick={onClose} className="btn-ghost p-1"><X size={18} /></button>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div className="col-span-2">
            <label className="label-eyebrow block mb-1">Candidate</label>
            <select
              data-testid="schedule-candidate"
              className="input"
              value={form.candidateId}
              onChange={(e) => setForm({ ...form, candidateId: e.target.value })}
            >
              {candidates.map((c) => (
                <option key={c.id} value={c.id}>#{c.id} · {c.name} — {c.position}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="label-eyebrow block mb-1">Date</label>
            <input data-testid="schedule-date" type="date" className="input" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} />
          </div>
          <div>
            <label className="label-eyebrow block mb-1">Time</label>
            <input data-testid="schedule-time" type="time" className="input" value={form.time} onChange={(e) => setForm({ ...form, time: e.target.value })} />
          </div>
          <div>
            <label className="label-eyebrow block mb-1">Duration (min)</label>
            <input type="number" min="15" step="15" className="input" value={form.durationMinutes} onChange={(e) => setForm({ ...form, durationMinutes: e.target.value })} />
          </div>
          <div>
            <label className="label-eyebrow block mb-1">Mode</label>
            <select className="input" value={form.mode} onChange={(e) => setForm({ ...form, mode: e.target.value })}>
              {["Video", "On-site", "Phone"].map((m) => <option key={m}>{m}</option>)}
            </select>
          </div>
          <div className="col-span-2">
            <label className="label-eyebrow block mb-1">Location / Video Link</label>
            <input className="input" placeholder="Zoom / Teams URL, or room name" value={form.location} onChange={(e) => setForm({ ...form, location: e.target.value })} />
          </div>
          <div className="col-span-2">
            <label className="label-eyebrow block mb-1">Interview Panel</label>
            <select
              data-testid="schedule-panel-select"
              className="input"
              value={form.panelId}
              onChange={(e) => setForm({ ...form, panelId: e.target.value })}
            >
              <option value="">— Choose a saved panel —</option>
              {panels.map((p) => (
                <option key={p.id} value={p.id}>{p.name} ({p.members?.length || 0} members)</option>
              ))}
            </select>
            {form.panelId && (
              <div className="text-[11px] text-slate-500 mt-1">
                Members: {panels.find(p => p.id === form.panelId)?.members?.map(m => m.name).join(", ") || "—"}
              </div>
            )}
          </div>
          <div className="col-span-2">
            <label className="label-eyebrow block mb-1">Or override with ad-hoc names (comma-separated)</label>
            <input data-testid="schedule-panel-override" className="input" placeholder="Only use if no saved panel fits" value={form.panelStrOverride} onChange={(e) => setForm({ ...form, panelStrOverride: e.target.value })} />
          </div>
          <div className="col-span-2">
            <label className="label-eyebrow block mb-1">Notes</label>
            <textarea className="input" value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} />
          </div>
        </div>
        {result && (
          <div className="mt-3 rounded-md border border-emerald-200 bg-emerald-50 p-3 text-sm text-emerald-800" data-testid="schedule-result">
            ✅ Interview scheduled. Candidate status → Interview.
            {result.emailInvite?.sent && (
              <div className="text-xs text-emerald-700 mt-1">
                📧 Calendar invite emailed to {(result.emailInvite.to || []).join(", ")}
              </div>
            )}
            {result.emailInvite && !result.emailInvite.sent && !result.emailInvite.skipped && (
              <div className="text-xs text-amber-700 mt-1">
                📧 Email invite not sent: {result.emailInvite.error || "unknown"}
              </div>
            )}
          </div>
        )}
        {error && (
          <div className="mt-3 rounded-md border border-red-300 bg-red-50 p-3 text-sm text-red-800">
            {error}
          </div>
        )}
        <div className="mt-5 flex justify-end gap-2">
          <button className="btn btn-outline" onClick={onClose}>Cancel</button>
          <button data-testid="schedule-save" className="btn btn-primary" onClick={save} disabled={saving}>
            {saving ? "Saving…" : "Schedule Interview"}
          </button>
        </div>
      </div>
    </div>
  );
}

function PanelsManager({ panels, onClose, onChanged }) {
  const [editing, setEditing] = useState(null); // null | {} | panel
  return (
    <div className="fixed inset-0 z-50 grid place-items-center bg-black/40" data-testid="panels-manager">
      <div className="card w-full max-w-2xl p-6 max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between mb-4">
          <h2 className="font-display text-xl font-bold flex items-center gap-2">
            <Users size={20} weight="fill" className="text-[#002FA7]" /> Interview Panels
          </h2>
          <div className="flex gap-2">
            <button data-testid="panel-new" className="btn btn-primary !py-1.5 !text-xs" onClick={() => setEditing({ name: "", description: "", members: [] })}>
              <Plus size={12} weight="bold" /> New Panel
            </button>
            <button onClick={onClose} className="btn-ghost p-1"><X size={18} /></button>
          </div>
        </div>
        {editing ? (
          <PanelEditor
            panel={editing}
            onCancel={() => setEditing(null)}
            onSaved={async () => { setEditing(null); await onChanged(); }}
          />
        ) : (
          <div>
            {panels.length === 0 ? (
              <div className="text-center text-slate-400 py-12 text-sm">
                No panels saved yet. Create one to reuse the same interviewers across multiple interviews.
              </div>
            ) : (
              <div className="divide-y">
                {panels.map((p) => (
                  <div key={p.id} className="py-3 flex items-start justify-between" data-testid={`panel-row-${p.id}`}>
                    <div>
                      <div className="font-semibold">{p.name}</div>
                      {p.description && <div className="text-xs text-slate-500">{p.description}</div>}
                      <div className="text-xs text-slate-600 mt-1">
                        {p.members?.length || 0} members: {(p.members || []).map(m => `${m.name}${m.email ? ` <${m.email}>` : ""}`).join(", ") || "—"}
                      </div>
                    </div>
                    <div className="flex gap-1">
                      <button
                        className="btn btn-outline !py-1 !px-2 !text-xs"
                        data-testid={`panel-edit-${p.id}`}
                        onClick={() => setEditing(p)}
                      >
                        <PencilSimple size={12} /> Edit
                      </button>
                      <button
                        className="btn btn-outline !py-1 !px-2 !text-xs !text-red-700 !border-red-300 hover:!bg-red-50"
                        data-testid={`panel-delete-${p.id}`}
                        onClick={async () => {
                          if (!window.confirm(`Delete panel "${p.name}"?`)) return;
                          await http.delete(`/panels/${p.id}`);
                          await onChanged();
                        }}
                      >
                        <Trash size={12} />
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

function PanelEditor({ panel, onCancel, onSaved }) {
  const [form, setForm] = useState({
    name: panel.name || "",
    description: panel.description || "",
    members: panel.members?.length ? panel.members : [{ name: "", email: "", role: "" }],
  });
  const [saving, setSaving] = useState(false);

  const setMember = (i, field, value) => {
    setForm((f) => ({ ...f, members: f.members.map((m, idx) => idx === i ? { ...m, [field]: value } : m) }));
  };
  const addMember = () => setForm((f) => ({ ...f, members: [...f.members, { name: "", email: "", role: "" }] }));
  const removeMember = (i) => setForm((f) => ({ ...f, members: f.members.filter((_, idx) => idx !== i) }));

  const save = async () => {
    setSaving(true);
    try {
      const body = { ...form, members: form.members.filter((m) => m.name.trim()) };
      if (panel.id) {
        await http.patch(`/panels/${panel.id}`, body);
      } else {
        await http.post("/panels", body);
      }
      await onSaved();
    } finally { setSaving(false); }
  };

  return (
    <div className="space-y-3" data-testid="panel-editor">
      <div>
        <label className="label-eyebrow block mb-1">Panel Name</label>
        <input data-testid="panel-name" className="input" placeholder="e.g. Engineering – Senior Hires" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
      </div>
      <div>
        <label className="label-eyebrow block mb-1">Description (optional)</label>
        <input className="input" placeholder="e.g. For all senior engineering roles" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} />
      </div>
      <div>
        <label className="label-eyebrow block mb-1">Members</label>
        <div className="space-y-2">
          {form.members.map((m, i) => (
            <div key={i} className="grid grid-cols-12 gap-2" data-testid={`panel-member-${i}`}>
              <input className="input col-span-4 !py-1.5 !text-xs" placeholder="Name" value={m.name} onChange={(e) => setMember(i, "name", e.target.value)} />
              <input className="input col-span-5 !py-1.5 !text-xs" placeholder="Email" value={m.email} onChange={(e) => setMember(i, "email", e.target.value)} />
              <input className="input col-span-2 !py-1.5 !text-xs" placeholder="Role" value={m.role} onChange={(e) => setMember(i, "role", e.target.value)} />
              <button className="btn btn-outline col-span-1 !py-1 !text-xs" onClick={() => removeMember(i)}><X size={12} /></button>
            </div>
          ))}
        </div>
        <button className="btn btn-outline !text-xs mt-2" onClick={addMember}><Plus size={12} /> Add member</button>
      </div>
      <div className="flex justify-end gap-2 pt-3 border-t">
        <button className="btn btn-outline" onClick={onCancel}>Cancel</button>
        <button data-testid="panel-save" className="btn btn-primary" onClick={save} disabled={saving || !form.name.trim()}>
          {saving ? "Saving…" : "Save Panel"}
        </button>
      </div>
    </div>
  );
}
